<script>
import Vue from 'vue'
import { GlobalBus } from '@services/bus'
import { MODAL } from '@constants/Text'
import ClientTags from '@modules/crm/components/client-tags/client-tags.vue'
import Title from '@components/section-title'
import { RECOMENDACAO_OPTIONS, RECOMENDACAO_COLORS, RECOMENDACAO_LABELS } from '@modules/consolidator/constants/recomendacao'

export default {
  name: 'SimulatorEditNotesModal',
  components: {
    ClientTags,
    Title,
  },
  props: {
    titleText: {
      type: String,
      default: 'Editar comentário e tags',
    },
  },
  data() {
    return {
      busModal: new Vue(),
      assetItem: {},
      commentText: '',
      setorText: '',
      recomendacaoValue: null,
      recomendacaoOptions: RECOMENDACAO_OPTIONS,
      recomendacaoColors: RECOMENDACAO_COLORS,
      recomendacaoLabels: RECOMENDACAO_LABELS,
      tagsList: [],
      saveHandler: null,
    }
  },
  created() {
    GlobalBus.$on(MODAL.PORTFOLIO_REVIEW_EDIT_NOTES, this.open)
  },
  beforeDestroy() {
    GlobalBus.$off(MODAL.PORTFOLIO_REVIEW_EDIT_NOTES, this.open)
  },
  methods: {
    open({ asset, onSave } = {}) {
      const targetAsset = asset || {}
      this.saveHandler = typeof onSave === 'function' ? onSave : null
      this.assetItem = targetAsset
      this.commentText = this.extractComment(targetAsset)
      this.setorText = targetAsset.setor || ''
      this.recomendacaoValue = targetAsset.recomendacao || null
      this.tagsList = this.extractTags(targetAsset)
      this.busModal.$emit('dialog', true)
    },
    close() {
      this.busModal.$emit('dialog', false)
      this.assetItem = {}
      this.commentText = ''
      this.setorText = ''
      this.recomendacaoValue = null
      this.tagsList = []
      this.saveHandler = null
    },
    async handleSave() {
      if (!this.hasAssetItem()) {
        this.close()
        return
      }

      const payload = this.buildSavePayload()
      this.applyPayloadToAsset(payload)

      try {
        await this.dispatchSave(payload)
      } finally {
        this.close()
      }
    },
    hasAssetItem() {
      return !!(this.assetItem && Object.keys(this.assetItem).length)
    },
    async dispatchSave(payload) {
      if (this.saveHandler) {
        await this.saveHandler(payload)
        return
      }
      this.$emit('save-notes', payload)
    },
    applyPayloadToAsset({ asset, comentario, setor, recomendacao, tags } = {}) {
      if (!asset) {
        return
      }

      const commentValue = typeof comentario === 'string' ? comentario : ''
      const setorValue = typeof setor === 'string' ? setor : ''
      const tagsList = Array.isArray(tags) ? tags : []

      this.$set(asset, 'comentario', commentValue)
      this.$set(asset, 'setor', setorValue)
      this.$set(asset, 'recomendacao', recomendacao || null)
      this.$set(asset, 'tags', tagsList)
    },
    buildSavePayload() {
      const commentValue = this.commentText || ''
      const setorValue = this.setorText || ''
      const recomendacaoVal = this.recomendacaoValue || null
      const tagsList = this.getValidTags()
      return {
        asset: this.assetItem,
        comentario: commentValue,
        setor: setorValue,
        recomendacao: recomendacaoVal,
        tags: tagsList,
      }
    },
    handleAddTag(newTag) {
      const tagValue = (newTag || '').trim()
      if (!tagValue) {
        return
      }

      const baseTags = Array.isArray(this.tagsList) ? this.tagsList : []
      if (!baseTags.includes(tagValue)) {
        this.tagsList = baseTags.concat(tagValue)
      }
    },
    handleRemoveTag(tagToRemove) {
      const baseTags = Array.isArray(this.tagsList) ? this.tagsList : []
      this.tagsList = baseTags.filter((tag) => tag !== tagToRemove)
    },
    getValidTags() {
      const baseTags = Array.isArray(this.tagsList) ? this.tagsList : []
      return baseTags.filter((tag) => {
        return typeof tag === 'string' && tag.trim()
      })
    },
    extractComment(asset) {
      if (!asset || typeof asset.comentario !== 'string') {
        return ''
      }
      return asset.comentario
    },
    extractTags(asset) {
      if (!asset || !Array.isArray(asset.tags)) {
        return []
      }
      return asset.tags.filter((tag) => {
        return typeof tag === 'string' && tag.trim()
      })
    },
  },
}
</script>

<template>
  <BaseModal :bus="busModal" persistent>
    <template v-slot:title>
      <Title :title="titleText" underlined />
    </template>

    <div class="simulator-edit-notes d-flex flex-column az-gap">
      <div>
        <div class="az-text-small mb-2"> Recomendação </div>
        <v-select
          v-model="recomendacaoValue"
          :items="recomendacaoOptions"
          outlined
          dense
          hide-details
          clearable
          class="new-ux"
          placeholder="Selecione..."
        >
          <template v-slot:selection="{ item: selectedOption }">
            <v-chip
              x-small
              :color="recomendacaoColors[selectedOption.value] || 'grey'"
              dark
            >
              {{ recomendacaoLabels[selectedOption.value] || selectedOption.text }}
            </v-chip>
          </template>
        </v-select>
      </div>

      <div>
        <div class="az-text-small mb-2"> Setor </div>
        <input-by-type
          v-model="setorText"
          type="text"
          outlined
          hide-details
          class="new-ux"
        />
      </div>

      <div class="simulator-edit-notes__tags">
        <div class="az-text-small mb-2"> Tags </div>
        <ClientTags
          :tags="tagsList"
          canEdit
          showTextNoTag
          @add-tag="handleAddTag"
          @remove-tag="handleRemoveTag"
        />
      </div>

      <div class="simulator-edit-notes__description">
        <div class="az-text-small mb-2"> Comentário </div>
        <input-by-type
          v-model="commentText"
          type="textarea"
          outlined
          hide-details
          class="new-ux"
        />
      </div>
    </div>

    <v-divider class="mt-2"></v-divider>

    <template v-slot:actions>
      <BaseButton underlined @click="close"> Cancelar </BaseButton>
      <BaseButton primary @click="handleSave"> Salvar </BaseButton>
    </template>
  </BaseModal>
</template>

<style lang="sass" scoped>
.simulator-edit-notes
  &__description
    background: #F5F5F5
    padding: 20px
    border-radius: 8px
    color: #5E5E5E
</style>
